import type { User } from "../../../slices/authSlice"
import { useState, useEffect } from "react";
import { SpinnerText } from "../../ui/Spinner";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

import NaturalApplicantForm from "./NaturalApplicantForm";
import LegalApplicantForm from "./LegalApplicantForm";
import SavedApplicantBox from "./SavedApplicantBox";
import { FiBriefcase, FiSearch, FiPlus, FiX } from "react-icons/fi";

import { useResetListener } from "../../../hooks/useResetListener";
import { searchEuipoPersons } from "../../../services/euipoPersonsService";

export default function AddApplicantBox({
  onChange,
  initialData,
  orderingParty,
  disabled = false,
  personLabel = "Applicant",
  searchPlaceholder = "Search applicant in EUIPO",
}: {
  onChange?: (list: any[]) => void;
  orderingParty?: Partial<User> | null
  initialData?: any[] | null;
  disabled?: boolean;
  personLabel?: string;
  searchPlaceholder?: string;
}) {
  const currentUser = useSelector((state: RootState) => state.auth.user)
  const user = orderingParty === undefined ? currentUser : orderingParty;

  const hasCompany = user?.company && user.company.trim().length > 0;

  const displayName = user
    ? `${user.title ? user.title + " " : ""}${user.firstName} ${user.lastName}`
    : "";

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingApplicantIndex, setEditingApplicantIndex] = useState<
    number | null
  >(null);
  const [savedApplicants, setSavedApplicants] = useState<any[]>(
    initialData || [],
  );

  const [newApplicantType, setNewApplicantType] = useState<"natural" | "legal">(
    "natural",
  );

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setSavedApplicants(initialData);
    }
  }, [initialData]);

  const emitApplicantsChange = (list: any[]) => {
    const cleaned = list
      .filter((a) => !a.isDeleted)
      .map(({ isDeleted, ...rest }) => rest);

    onChange?.(cleaned);
  };

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    searchEuipoPersons(query)
      .then((data) => setResults(data || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query]);

  useResetListener(() => {
    setSavedApplicants([]);
    setEditingApplicantIndex(null);
    setIsFormOpen(false);
    setQuery("");
    setResults([]);
    emitApplicantsChange([]);
  });

  const splitNaturalName = (fullName: string) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const parts = fullName.trim().split(" ");
    return {
      firstName: parts.slice(0, -1).join(" "),
      lastName: parts.slice(-1)[0] || "",
    };
  };

  const handleImportClick = (hit: any) => {
    let next: any;

    if (hit.type === "natural") {
      const { firstName, lastName } = splitNaturalName(hit.name);
      next = {
        type: "natural",
        euipoPersonIdentifier: hit.personId,
        firstName,
        lastName,
        address: {
          city: hit.city || "",
          country: hit.country || "",
        },
      };
    } else {
      next = {
        type: "legal",
        euipoPersonIdentifier: hit.personId,
        legalName: hit.name || "",
        address: {
          city: hit.city || "",
          country: hit.country || "",
        },
      };
    }

    const updated = [...savedApplicants, next];
    setSavedApplicants(updated);
    emitApplicantsChange(updated);

    setQuery("");
    setResults([]);
  };

  const handleSave = (formData: any) => {
    const updated = editingApplicantIndex !== null
      ? savedApplicants.map((applicant, index) => index === editingApplicantIndex ? { ...applicant, ...formData } : applicant)
      : [...savedApplicants, formData];
    setSavedApplicants(updated);
    emitApplicantsChange(updated);

    setIsFormOpen(false);
    setEditingApplicantIndex(null);
  };

  const currentApplicant =
    editingApplicantIndex !== null
      ? savedApplicants[editingApplicantIndex]
      : null;

  const currentType = currentApplicant?.type ?? newApplicantType;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {user && (
          <div className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6edf5] text-dantzBlue">
                <FiBriefcase size={18} />
              </span>

              <div className="relative min-w-0 flex-1">
                <span className="absolute right-0 top-0 rounded-full bg-[#e6edf5] px-2 py-0.5 text-[10px] font-medium text-dantzBlue">
                  Auto-filled
                </span>

                {hasCompany && (
                  <p className="mt-0.5 truncate pr-16 text-sm font-medium text-gray-800">
                    {user.company}
                  </p>
                )}

                <p className="truncate pr-16 text-xs text-gray-600">{displayName}</p>

                {hasCompany ? (
                  <>
                    {user.companyStreet && (
                      <p className="truncate text-xs text-gray-600">
                        {user.companyStreet}
                      </p>
                    )}
                    <p className="truncate text-xs text-gray-600">
                      {user.companyCity ?? "-"}, {user.companyCountry ?? "-"}
                    </p>
                  </>
                ) : (
                  <>
                    {user.street && (
                      <p className="truncate text-xs text-gray-600">
                        {user.street}
                      </p>
                    )}
                    <p className="truncate text-xs text-gray-600">
                      {user.city ?? "-"}, {user.country ?? "-"}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {savedApplicants.map(
          (applicant, index) =>
            !applicant.isDeleted && (
              <SavedApplicantBox
                key={index}
                applicant={applicant}
                personLabel={personLabel}
                onEdit={
                  !applicant.euipoPersonIdentifier
                    ? () => {
                        setEditingApplicantIndex(index);
                        setIsFormOpen(true);
                      }
                    : undefined
                }
                onDelete={() => {
                  const updated = savedApplicants.filter((_, i) => i !== index);
                  setSavedApplicants(updated);
                  emitApplicantsChange(updated);
                }}
              />
            ),
        )}
      </div>

      <div>
        <div className="relative">
          <FiSearch
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={query}
            onChange={(e) => {
              if (!disabled) setQuery(e.target.value);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-dantzBlue focus:outline-none"
            disabled={disabled}
          />
        </div>

        {loading && <p className="mt-2 text-xs text-gray-500"><SpinnerText>Searching…</SpinnerText></p>}

        {!loading && results.length > 0 && (
          <div className="mt-2 divide-y rounded-lg border border-gray-200 bg-white">
            {results.map((r) => (
              <div
                key={r.personId}
                onClick={() => {
                  if (!disabled) handleImportClick(r);
                }}
                className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-50"
              >
                <div className="font-medium text-gray-800">{r.name}</div>
                <div className="text-xs text-gray-600">
                  {r.city || "-"}, {r.country || "-"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={disabled}
        aria-expanded={isFormOpen}
        onClick={() => {
          if (!disabled) {
            if (isFormOpen) {
              setIsFormOpen(false);
              return;
            }
            setEditingApplicantIndex(null);
            setNewApplicantType("natural");
            setIsFormOpen(true);
          }
        }}
        className="flex w-full flex-col items-center justify-center rounded-xl border-[1.5px] border-dashed border-gray-300 bg-gray-50/60 p-5 text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiPlus size={20} />
        <span className="mt-1 text-sm font-medium">New {personLabel}</span>
        <span className="mt-0.5 text-[11px] text-gray-400">
          Add a natural person or legal entity
        </span>
      </button>

      {isFormOpen && (
        <div className="border border-gray-200 rounded-2xl bg-white p-6 shadow-sm max-w-full space-y-6">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-800">{editingApplicantIndex === null ? "New" : "Edit"} {personLabel}</h3>
            <button type="button" aria-label="Close person form" onClick={() => setIsFormOpen(false)} className="rounded-md p-2 text-gray-500 hover:bg-gray-100"><FiX aria-hidden="true" /></button>
          </div>
          {editingApplicantIndex === null && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {[
                { value: "natural", label: "Natural person" },
                { value: "legal", label: "Legal entity" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                    newApplicantType === option.value
                      ? "border-dantzBlue bg-blue-50 text-dantzBlue"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    checked={newApplicantType === option.value}
                    onChange={() =>
                      setNewApplicantType(option.value as "natural" | "legal")
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          )}

          {currentType === "natural" ? (
            <NaturalApplicantForm
              onCancel={() => setIsFormOpen(false)}
              onSave={handleSave}
              initialValues={currentApplicant}
            />
          ) : (
            <LegalApplicantForm
              onCancel={() => setIsFormOpen(false)}
              onSave={handleSave}
              initialValues={currentApplicant}
            />
          )}
        </div>
      )}
    </div>
  );
}
